import React, { useCallback } from 'react';

import { useTranslation } from '@lib/useTypedTranslation';
import Card from '../../../../components/card/card';
import { InitialTableState, useTableReducer } from '../../../../components/data-table/lib';
import { List } from '../../../../components/list/list';
import { BreadcrumbLink } from '../../../../components/navigation/breadcrumbLink';
import { useUserSettingsContext } from '../../../../context/userSettings';
import { useWorldRequest } from '../../../../lib/useWorldRequest';
import { useWorldAction } from '../../../../lib/useWorldAction';
import { useWorldRoutes } from '../../../../routes/parts/allWorldRoutes';
import { getUserTimeZone } from '../../../../lib/timeZone';
import { formatDateAsOrdinalWithFullMonthAndYear } from '../../../../lib/ordinal';
import { convertFilterToQuery, IFilterResult } from '../../../../components/filters/filterLib';
import { nullGroupFilter, nullHomeLocationFilter } from '../../../../components/filters/devicesFilterLib';
import { FilterContext } from '@components/data-table/filters/context';
import {
  IPerformanceEventDeviceRow,
  IPerformanceEventDevicesListData,
  getCorePerformanceEventDevicesList,
  exportCorePerformanceEventDevicesListAction
} from '../../../../services/core/performanceEventDevices';
import { DeviceEventCountName } from '../../../../services/core/eventsTypes';
import { getEventsChartData } from '../charts/lib';
import { useCorePerformanceDevicesColumns } from './useCorePerformanceDevicesColumns';
import { useCorePerformanceDevicesFilterOptions } from './filters/useCorePerformanceDevicesFilterOptions';
import useNormalizedHeaderButtons from '../../../../components/controls/normalizedHeaderButtons';

import './corePerformanceEventDevices.css';

interface IProps {
  name: DeviceEventCountName,
  utc: number,
  homeLocationId?: string | null,
  group?: string | null,
  zoneId?: string | null,
  model?: string | null
}

const tableName = 'corePerformanceEventDevicesList';
const defaultPageSize = 20;

export const getCorePerformanceDevicesColumnFromFilterGroup = (filterGroup: string): string => {
  const baseGroup = filterGroup.endsWith('_negate') ? filterGroup.slice(0, -'_negate'.length) : filterGroup;
  switch (baseGroup) {
  case 'manufacturers':
    return 'manufacturer';
  case 'models':
    return 'model';
  case 'groups':
    return 'group';
  case 'homeLocations':
    return 'homeLocation';
  default:
    throw new Error('Unknown filter group: ' + filterGroup);
  }
};

// No shared "no model" constant exists (unlike home location/group), since there's no such
// option in the Model column's filter dropdown. It's only ever used as an internal marker so an
// explicit `model: null` carried from the Performance page still round-trips through `filter`.
const nullModelFilter = { id: 'no-model', nullFilter: true };

export function getInitialCorePerformanceDevicesFilter(homeLocationId?: string | null, group?: string | null, model?: string | null): IFilterResult {
  const filter: IFilterResult = {};
  if (homeLocationId !== undefined) {
    filter.homeLocations = [homeLocationId === null ? nullHomeLocationFilter : homeLocationId];
  }
  if (group !== undefined) {
    filter.groups = [group === null ? nullGroupFilter : group];
  }
  if (model !== undefined) {
    filter.models = [model === null ? nullModelFilter : model];
  }
  return filter;
}

export function CorePerformanceDevices(props: IProps) {
  const { name, utc, homeLocationId, group, zoneId, model } = props;
  const timeZone = getUserTimeZone();
  const { t } = useTranslation(['translation', 'performance']);
  const routes = useWorldRoutes();
  const { tablePageSizes } = useUserSettingsContext();

  const { translationKey } = getEventsChartData(name);
  const eventName = t(translationKey as any, { ns: 'performance' });
  const date = formatDateAsOrdinalWithFullMonthAndYear(utc, timeZone);
  const title = t('PERFORMANCE_DEVICES_TITLE', { ns: 'performance', eventName, date });

  const columns = useCorePerformanceDevicesColumns({ countColumnTitle: eventName });

  const initialTableState: InitialTableState = {
    initialRows: 4,
    limit: tablePageSizes?.[tableName] || defaultPageSize,
    sort: { column: columns.findIndex((c) => c.id === 'count'), direction: 'desc', field: 'count' },
    filter: getInitialCorePerformanceDevicesFilter(homeLocationId, group, model)
  };

  const [tableReducerProperties, tableReducerFunctions] = useTableReducer<IPerformanceEventDeviceRow>(tableName, initialTableState);
  const [
    { offset, limit, sort, filter, data, isLoading },
    { onFilter, onDataLoaded }
  ] = [tableReducerProperties, tableReducerFunctions];

  const listFetcher = useCallback(() => {
    return getCorePerformanceEventDevicesList({
      name,
      utc,
      timeZone,
      zoneId,
      limit,
      offset,
      sort: { field: sort.field, order: sort.direction },
      filter: convertFilterToQuery(filter)
    });
  }, [name, utc, timeZone, zoneId, limit, offset, sort, filter]);

  const exportCorePerformanceDevices = useWorldAction(exportCorePerformanceEventDevicesListAction);
  const onExportClicked = async (): Promise<string> => {
    return exportCorePerformanceDevices({
      name,
      utc,
      timeZone,
      zoneId,
      limit: 10000,
      offset: 0,
      sort: { field: sort.field, order: sort.direction },
      filter: convertFilterToQuery(filter)
    });
  };

  const additionalHeaderButtons = useNormalizedHeaderButtons({
    export: {
      exportFunction: onExportClicked,
      filename: `${title}.csv`,
      disabled: isLoading || !data?.length
    }
  });

  const { availableFilters, filtersLoaded } = useCorePerformanceDevicesFilterOptions();

  const handleFiltersUpdated = (newFilter: IFilterResult) => {
    onFilter(newFilter);
  };
  const handleCancel = (): void => { /* no-op */ };

  const translationItems = {
    statusText: {
      key: 'DEVICE',
      ns: 'translation'
    },
    tableTitle: {
      text: title
    }
  };

  const breadcrumb = (
    <div data-id="core-performance-devices-breadcrumb" className="core-performance-devices-breadcrumb">
      <BreadcrumbLink to={routes.core.performance} aria-label="Link to performance" className="core-performance-devices-breadcrumb-link">{t('PERFORMANCE', { ns: 'translation' })}</BreadcrumbLink>
      <i className="fas fa-chevron-right" />
      <div className="core-performance-devices-event-name">{eventName}</div>
    </div>
  );

  return (
    <FilterContext.Provider value={{
      getColumnFromFilterGroup: getCorePerformanceDevicesColumnFromFilterGroup,
      filters: filter,
      filterOptions: { availableFilters, filtersLoaded },
      handleFiltersUpdated,
      handleCancel
    }}>
      <div>
        <div data-id="core-performance-devices" className="core-performance-list">
          <Card noPadding={true}>
            <List<IPerformanceEventDeviceRow, IPerformanceEventDevicesListData, 'totalCount', 'list', 'id'>
              dataId='core_performance_devices_list'
              dataTableEnabled={true}
              customHeader={false}
              countDataPath='totalCount'
              listDataPath='list'
              hasCheckboxes={false}
              hasFilters={true}
              hasSearch={false}
              translationItems={translationItems}
              subHeader={breadcrumb}
              columns={columns}
              fetcher={listFetcher}
              tableReducerFunctions={tableReducerFunctions}
              tableReducerProperties={tableReducerProperties}
              useRequestHook={useWorldRequest}
              options={{ rowIdDataPath: 'id' }}
              additionalHeaderButtons={additionalHeaderButtons}
            />
          </Card>
        </div>
      </div>
    </FilterContext.Provider>
  );
}
